/**
 * Module dependencies.
 */

import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react';

import { useRouter } from 'next/router';
import { useSort } from 'src/context/sort/context';
import BaseSidebar from 'src/components/filters/base/base-sidebar';
import qs from 'qs';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  isUpdating: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

/**
 * `BaseSortSidebar` component.
 */

const BaseSortSidebar = (props: Props): ReactElement => {
  const { children, className, isOpen, isUpdating, onClose, onUpdate } = props;
  const { omitUrl, onClear, onSubmit, reset } = useSort();
  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const { translate } = useTranslate();
  const router = useRouter();
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleUpdate = useCallback(
    (sort: string | null) => {
      if (omitUrl) {
        onClose();
        setForceUpdate(true);

        return;
      }

      router
        .push(
          {
            pathname: router.pathname,
            query: qs.stringify({ sort })
          },
          undefined,
          { shallow: true }
        )
        .then(() => {
          onUpdate();
          onClose();
        });
    },
    [omitUrl, onClose, onUpdate, router]
  );

  const handleClear = useCallback(() => {
    const { submittedSort } = onClear();

    handleUpdate(submittedSort);
  }, [handleUpdate, onClear]);

  const handleSubmit = useCallback(() => {
    const { submittedSort } = onSubmit();

    handleUpdate(submittedSort);
  }, [handleUpdate, onSubmit]);

  useEffect(() => {
    if (omitUrl && forceUpdate) {
      const timeout = setTimeout(() => {
        onUpdate();
        setForceUpdate(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [forceUpdate, omitUrl, onUpdate]);

  return (
    <BaseSidebar
      className={className}
      isOpen={isOpen}
      isUpdating={isUpdating}
      onClear={handleClear}
      onClose={handleClose}
      onSubmit={handleSubmit}
      title={translate('common:actions.sortBy')}
    >
      {children}
    </BaseSidebar>
  );
};

/**
 * Export `BaseSortSidebar` component.
 */

export default BaseSortSidebar;
